import {
  CRITERIOS_CALCULADOS,
  PERIODICIDADES_BONUS,
  configuracaoAvaliacaoSchema,
  criterioAvaliacaoSchema,
  faixaPremiacaoSchema,
  pesoCriterioSchema,
} from '@projetive-op/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, getRouteApi } from '@tanstack/react-router'
import { Trash2 } from 'lucide-react'
import { useState } from 'react'

import {
  BarraTitulo,
  BotaoEditar,
  Formulario,
  RodapeGaveta,
  TituloSecao,
  aoSubmeter,
  useGavetaCadastro,
} from '@/components/cadastros/base'
import { Button } from '@/components/ui/button'
import { Campo, Checkbox, Input, Select } from '@/components/ui/campo'
import { EsqueletoLinhas } from '@/components/ui/estado'
import { Gaveta } from '@/components/ui/gaveta'
import {
  Ativo,
  Cabecalho,
  Carregando,
  Linha,
  Pilula,
  Tabela,
  Td,
  Th,
  Vazio,
} from '@/components/ui/tabela'
import { toast } from '@/components/ui/toast'
import { mensagemDeErro } from '@/lib/erros'
import { nota } from '@/lib/formato'
import type { ErrosFormulario } from '@/lib/formulario'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/avaliacao')({ component: Avaliacao })

const gestaoRoute = getRouteApi('/gestao')

const ROTULO_PERIODICIDADE: Record<(typeof PERIODICIDADES_BONUS)[number], string> = {
  SEMANAL: 'Semanal (segunda a domingo)',
  QUINZENAL: 'Quinzenal (1–15 e 16–fim do mês)',
  MENSAL: 'Mensal',
}
const ROTULO_CRITERIO: Record<(typeof CRITERIOS_CALCULADOS)[number], string> = {
  eficiencia: 'Eficiência (previsto ÷ real das fichas concluídas)',
  produtividade: 'Produtividade (tempo produzindo ÷ jornada)',
  pontualidade: 'Pontualidade (primeiro login × início do turno)',
}
const calculado = (codigo: string) => (CRITERIOS_CALCULADOS as readonly string[]).includes(codigo)

type Criterio = NonNullable<ReturnType<typeof useCriterios>['data']>[number]
type Faixa = NonNullable<ReturnType<typeof useFaixas>['data']>[number]
type Peso = Criterio['pesos'][number] & { criterioId: string }

function useCriterios() {
  const trpc = useTRPC()
  return useQuery(trpc.avaliacao.criterios.listar.queryOptions())
}
function useFaixas() {
  const trpc = useTRPC()
  return useQuery(trpc.avaliacao.faixas.listar.queryOptions())
}

/**
 * Régua do bônus (RF-D01, D04, D05, D06): critérios e pesos, pesos por
 * setor/produto, faixas e parâmetros. Gestão vê; só a diretoria altera —
 * "o sistema calcula, o cliente decide".
 */
function Avaliacao() {
  const { me } = gestaoRoute.useRouteContext()
  const podeEditar = me.perfil === 'DIRETORIA'
  return (
    <>
      <BarraTitulo
        titulo="Avaliação e premiação"
        descricao={
          podeEditar
            ? 'Critérios, pesos, faixas e parâmetros da nota. O sistema calcula; quem decide o bônus é a gestão.'
            : 'Critérios, pesos, faixas e parâmetros da nota. Somente a diretoria altera estes valores.'
        }
      />
      <div className="flex flex-col gap-10">
        <Criterios podeEditar={podeEditar} />
        <PesosPorSetorProduto podeEditar={podeEditar} />
        <Faixas podeEditar={podeEditar} />
        <Configuracao podeEditar={podeEditar} />
      </div>
    </>
  )
}

function Criterios({ podeEditar }: { podeEditar: boolean }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const lista = useCriterios()
  const g = useGavetaCadastro<Criterio>()
  const invalidar = () =>
    qc.invalidateQueries({ queryKey: trpc.avaliacao.criterios.listar.queryKey() })
  const aoSalvar = {
    onSuccess: () => {
      void invalidar()
      g.fechar()
    },
  }
  const criar = useMutation(trpc.avaliacao.criterios.criar.mutationOptions(aoSalvar))
  const atualizar = useMutation(trpc.avaliacao.criterios.atualizar.mutationOptions(aoSalvar))
  const submit = aoSubmeter({
    schema: criterioAvaliacaoSchema,
    numericos: ['peso'],
    booleanos: ['ativo'],
    setErros: g.setErros,
    sucesso: (d) => `Critério ${d.nome} salvo.`,
    onValido: (d) =>
      g.item ? atualizar.mutateAsync({ id: g.item.id, ...d }) : criar.mutateAsync(d),
  })
  const somaPesos = lista.data?.filter((c) => c.ativo).reduce((n, c) => n + c.peso, 0) ?? 0

  return (
    <section aria-labelledby="criterios">
      <TituloSecao
        id="criterios"
        titulo="Critérios da nota"
        descricao="Nota = 1 + 9 × média ponderada. Bater a meta num critério vale o máximo nele."
        onNovo={podeEditar ? g.abrirNovo : undefined}
        rotuloNovo="Novo critério"
      />
      <Tabela>
        <Cabecalho>
          <Th>Critério</Th>
          <Th>Código</Th>
          <Th className="text-right">Peso</Th>
          <Th className="text-right">Participação</Th>
          <Th />
        </Cabecalho>
        <tbody>
          {lista.data?.map((c) => (
            <Linha key={c.id}>
              <Td className="font-medium">
                {c.nome} {!c.ativo && <Ativo ativo={false} />}
              </Td>
              <Td>
                <span className="font-mono text-xs">{c.codigo}</span>
                {!calculado(c.codigo) && (
                  <Pilula tom="amarelo" className="ml-2">
                    não calculado
                  </Pilula>
                )}
              </Td>
              <Td numerico className="text-right">
                {c.peso}
              </Td>
              <Td numerico className="text-right text-fg-muted">
                {c.ativo && somaPesos > 0 ? `${Math.round((c.peso / somaPesos) * 100)}%` : '—'}
              </Td>
              <Td className="text-right">
                {podeEditar && <BotaoEditar rotulo={c.nome} onClick={() => g.abrirEditar(c)} />}
              </Td>
            </Linha>
          ))}
          {lista.isPending && <Carregando colunas={5} />}
          {lista.data?.length === 0 && (
            <Vazio colunas={5}>Nenhum critério: sem critérios não há nota.</Vazio>
          )}
        </tbody>
      </Tabela>
      <Gaveta
        aberta={g.aberta}
        titulo={g.item ? 'Editar critério' : 'Novo critério'}
        onFechar={g.fechar}
        rodape={
          <RodapeGaveta
            form="form-criterio"
            salvando={criar.isPending || atualizar.isPending}
            onCancelar={g.fechar}
          />
        }
      >
        <Formulario
          id="form-criterio"
          onSubmit={submit}
          erros={g.erros}
          onLimparErro={g.limparErro}
        >
          <Campo label="Nome" erro={g.erros.nome}>
            {(p) => <Input {...p} name="nome" defaultValue={g.item?.nome} autoFocus />}
          </Campo>
          <Campo
            label="Código"
            erro={g.erros.codigo}
            dica={`O sistema calcula: ${CRITERIOS_CALCULADOS.join(', ')}. Outro código fica sem valor e fora da nota.`}
          >
            {(p) => (
              <Input {...p} name="codigo" defaultValue={g.item?.codigo} className="font-mono" />
            )}
          </Campo>
          <Campo label="Peso" erro={g.erros.peso} dica="Relativo aos outros; não precisa somar 1.">
            {(p) => (
              <Input
                {...p}
                name="peso"
                type="number"
                step="0.1"
                min={0}
                defaultValue={g.item?.peso ?? 1}
              />
            )}
          </Campo>
          <Checkbox name="ativo" label="Ativo" defaultChecked={g.item?.ativo ?? true} />
        </Formulario>
      </Gaveta>
      <ul className="mt-3 flex flex-col gap-1 text-xs text-fg-subtle">
        {CRITERIOS_CALCULADOS.map((c) => (
          <li key={c}>
            <span className="font-mono">{c}</span> — {ROTULO_CRITERIO[c]}
          </li>
        ))}
      </ul>
    </section>
  )
}

function PesosPorSetorProduto({ podeEditar }: { podeEditar: boolean }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const criterios = useCriterios()
  const setores = useQuery(trpc.cadastros.setores.listar.queryOptions())
  const produtos = useQuery(trpc.cadastros.produtos.listar.queryOptions())
  const g = useGavetaCadastro<Peso>()
  const invalidar = () =>
    qc.invalidateQueries({ queryKey: trpc.avaliacao.criterios.listar.queryKey() })
  const definir = useMutation(
    trpc.avaliacao.criterios.definirPeso.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )
  const remover = useMutation(
    trpc.avaliacao.criterios.removerPeso.mutationOptions({
      onSuccess: () => {
        toast.sucesso('Peso específico removido.')
        void invalidar()
      },
      onError: (e) => toast.erro(mensagemDeErro(e)),
    }),
  )
  const submit = aoSubmeter({
    schema: pesoCriterioSchema,
    numericos: ['peso'],
    opcionais: ['setorId', 'produtoId'],
    setErros: g.setErros,
    sucesso: 'Peso específico salvo.',
    onValido: (d) => definir.mutateAsync(d),
  })
  const pesos = (criterios.data ?? []).flatMap((c) =>
    c.pesos.map((p) => ({ ...p, criterioId: c.id, criterio: c })),
  )

  return (
    <section aria-labelledby="pesos">
      <TituloSecao
        id="pesos"
        titulo="Pesos por setor e produto"
        descricao="Sobrescrevem o peso padrão onde o critério pesa diferente (ex.: pontualidade na extrusora). Setor é o tipo de posto; o peso do dia é ponderado pelo tempo em cada um."
        onNovo={podeEditar ? g.abrirNovo : undefined}
        rotuloNovo="Novo peso específico"
      />
      <Tabela>
        <Cabecalho>
          <Th>Critério</Th>
          <Th>Setor</Th>
          <Th>Produto</Th>
          <Th className="text-right">Peso</Th>
          <Th />
        </Cabecalho>
        <tbody>
          {pesos.map((p) => (
            <Linha key={p.id}>
              <Td className="font-medium">{p.criterio.nome}</Td>
              <Td className="text-fg-muted">{p.setor?.nome ?? 'Todos'}</Td>
              <Td className="text-fg-muted">{p.produto?.nome ?? 'Todos'}</Td>
              <Td numerico className="text-right">
                {p.peso} <span className="text-xs text-fg-subtle">(padrão {p.criterio.peso})</span>
              </Td>
              <Td className="text-right">
                {podeEditar && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remover peso de ${p.criterio.nome} em ${p.setor?.nome ?? p.produto?.nome ?? ''}`}
                    onClick={() => remover.mutate({ id: p.id })}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                )}
              </Td>
            </Linha>
          ))}
          {criterios.isPending && <Carregando colunas={5} />}
          {criterios.data && pesos.length === 0 && (
            <Vazio colunas={5}>Nenhum peso específico: vale o padrão em toda a fábrica.</Vazio>
          )}
        </tbody>
      </Tabela>
      <Gaveta
        aberta={g.aberta}
        titulo="Peso por setor e produto"
        onFechar={g.fechar}
        rodape={
          <RodapeGaveta form="form-peso" salvando={definir.isPending} onCancelar={g.fechar} />
        }
      >
        <Formulario id="form-peso" onSubmit={submit} erros={g.erros} onLimparErro={g.limparErro}>
          <Campo label="Critério" erro={g.erros.criterioId}>
            {(p) => (
              <Select {...p} name="criterioId" defaultValue={criterios.data?.[0]?.id}>
                {criterios.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome} (padrão {c.peso})
                  </option>
                ))}
              </Select>
            )}
          </Campo>
          <Campo label="Setor" erro={g.erros.setorId} dica="Vazio = qualquer setor.">
            {(p) => (
              <Select {...p} name="setorId" defaultValue="">
                <option value="">Todos</option>
                {setores.data?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </Select>
            )}
          </Campo>
          <Campo label="Produto" erro={g.erros.produtoId} dica="Vazio = qualquer produto.">
            {(p) => (
              <Select {...p} name="produtoId" defaultValue="">
                <option value="">Todos</option>
                {produtos.data?.map((pr) => (
                  <option key={pr.id} value={pr.id}>
                    {pr.nome}
                  </option>
                ))}
              </Select>
            )}
          </Campo>
          <Campo label="Peso" erro={g.erros.peso} dica="0 tira o critério da nota nesse contexto.">
            {(p) => <Input {...p} name="peso" type="number" step="0.1" min={0} defaultValue={1} />}
          </Campo>
        </Formulario>
      </Gaveta>
    </section>
  )
}

function Faixas({ podeEditar }: { podeEditar: boolean }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const lista = useFaixas()
  const g = useGavetaCadastro<Faixa>()
  const invalidar = () =>
    qc.invalidateQueries({ queryKey: trpc.avaliacao.faixas.listar.queryKey() })
  const aoSalvar = {
    onSuccess: () => {
      void invalidar()
      g.fechar()
    },
  }
  const criar = useMutation(trpc.avaliacao.faixas.criar.mutationOptions(aoSalvar))
  const atualizar = useMutation(trpc.avaliacao.faixas.atualizar.mutationOptions(aoSalvar))
  const remover = useMutation(
    trpc.avaliacao.faixas.remover.mutationOptions({
      onSuccess: () => {
        toast.sucesso('Faixa removida.')
        void invalidar()
      },
      onError: (e) => toast.erro(mensagemDeErro(e)),
    }),
  )
  const submit = aoSubmeter({
    schema: faixaPremiacaoSchema,
    numericos: ['notaMinima'],
    setErros: g.setErros,
    sucesso: (d) => `Faixa ${d.nome} salva.`,
    onValido: (d) =>
      g.item ? atualizar.mutateAsync({ id: g.item.id, ...d }) : criar.mutateAsync(d),
  })

  return (
    <section aria-labelledby="faixas">
      <TituloSecao
        id="faixas"
        titulo="Faixas de premiação"
        descricao="O operador entra na faixa mais alta cuja nota mínima alcançou. O valor pago é decisão da gestão, fora do sistema."
        onNovo={podeEditar ? g.abrirNovo : undefined}
        rotuloNovo="Nova faixa"
      />
      <Tabela>
        <Cabecalho>
          <Th>Faixa</Th>
          <Th>Código</Th>
          <Th className="text-right">Nota mínima</Th>
          <Th />
        </Cabecalho>
        <tbody>
          {lista.data?.map((f) => (
            <Linha key={f.id}>
              <Td className="font-medium">{f.nome}</Td>
              <Td className="font-mono text-xs">{f.codigo}</Td>
              <Td numerico className="text-right">
                {nota(f.notaMinima)}
              </Td>
              <Td className="text-right">
                {podeEditar && (
                  <>
                    <BotaoEditar rotulo={f.nome} onClick={() => g.abrirEditar(f)} />
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remover ${f.nome}`}
                      onClick={() => remover.mutate({ id: f.id })}
                    >
                      <Trash2 className="size-4" aria-hidden />
                    </Button>
                  </>
                )}
              </Td>
            </Linha>
          ))}
          {lista.isPending && <Carregando colunas={4} />}
          {lista.data?.length === 0 && (
            <Vazio colunas={4}>Nenhuma faixa: a nota aparece sem classificação.</Vazio>
          )}
        </tbody>
      </Tabela>
      <Gaveta
        aberta={g.aberta}
        titulo={g.item ? 'Editar faixa' : 'Nova faixa'}
        onFechar={g.fechar}
        rodape={
          <RodapeGaveta
            form="form-faixa"
            salvando={criar.isPending || atualizar.isPending}
            onCancelar={g.fechar}
          />
        }
      >
        <Formulario id="form-faixa" onSubmit={submit} erros={g.erros} onLimparErro={g.limparErro}>
          <Campo label="Nome" erro={g.erros.nome}>
            {(p) => <Input {...p} name="nome" defaultValue={g.item?.nome} autoFocus />}
          </Campo>
          <Campo label="Código" erro={g.erros.codigo} dica="Minúsculas e _ (ex.: acima_da_media).">
            {(p) => (
              <Input {...p} name="codigo" defaultValue={g.item?.codigo} className="font-mono" />
            )}
          </Campo>
          <Campo label="Nota mínima" erro={g.erros.notaMinima} dica="De 1 a 10, com uma casa.">
            {(p) => (
              <Input
                {...p}
                name="notaMinima"
                type="number"
                step="0.1"
                min={1}
                max={10}
                defaultValue={g.item?.notaMinima ?? 7}
              />
            )}
          </Campo>
        </Formulario>
      </Gaveta>
    </section>
  )
}

function Configuracao({ podeEditar }: { podeEditar: boolean }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const config = useQuery(trpc.avaliacao.configuracao.obter.queryOptions())
  const [erros, setErros] = useState<ErrosFormulario>({})
  const salvar = useMutation(
    trpc.avaliacao.configuracao.salvar.mutationOptions({
      onSuccess: () =>
        qc.invalidateQueries({ queryKey: trpc.avaliacao.configuracao.obter.queryKey() }),
    }),
  )
  const submit = aoSubmeter({
    schema: configuracaoAvaliacaoSchema,
    numericos: [
      'curvaExperienciaDias',
      'toleranciaInicial',
      'semaforoVerdeMin',
      'semaforoAmareloMin',
      'pontualidadeToleranciaMin',
      'pontualidadeAtrasoMaximoMin',
      'faltasParaPerderFaixa',
    ],
    booleanos: [
      'exibirIndividualNoPainel',
      'exibirIndicadorAoOperador',
      'atestadoContaComoFalta',
      'faltaEntraNaNota',
    ],
    setErros,
    sucesso: 'Parâmetros salvos.',
    onValido: (d) => salvar.mutateAsync(d),
  })
  const c = config.data

  return (
    <section aria-labelledby="parametros">
      <h2 id="parametros" className="mb-1 text-lg font-semibold">
        Parâmetros
      </h2>
      <p className="mb-3 text-sm text-fg-muted">
        Curva dos 60 dias, semáforo, periodicidade do bônus, pontualidade, faltas e o que o operador
        vê.
      </p>
      <div className="max-w-2xl card p-6">
        {config.isPending && <EsqueletoLinhas linhas={6} rotulo="Carregando os parâmetros" />}
        {c && (
          <Formulario id="form-config-avaliacao" onSubmit={submit} erros={erros}>
            <fieldset disabled={!podeEditar} className="flex flex-col gap-4">
              <Campo label="Periodicidade do bônus" erro={erros.periodicidadeBonus}>
                {(p) => (
                  <Select {...p} name="periodicidadeBonus" defaultValue={c.periodicidadeBonus}>
                    {PERIODICIDADES_BONUS.map((v) => (
                      <option key={v} value={v}>
                        {ROTULO_PERIODICIDADE[v]}
                      </option>
                    ))}
                  </Select>
                )}
              </Campo>
              <div className="grid gap-4 sm:grid-cols-2">
                <Campo
                  label="Curva de experiência (dias)"
                  erro={erros.curvaExperienciaDias}
                  dica="0 desliga a curva."
                >
                  {(p) => (
                    <Input
                      {...p}
                      name="curvaExperienciaDias"
                      type="number"
                      min={0}
                      defaultValue={c.curvaExperienciaDias}
                    />
                  )}
                </Campo>
                <Campo
                  label="Tolerância inicial (fração)"
                  erro={erros.toleranciaInicial}
                  dica="0,30 = novato no 1º dia é avaliado 30% mais brando."
                >
                  {(p) => (
                    <Input
                      {...p}
                      name="toleranciaInicial"
                      type="number"
                      step="0.01"
                      min={0}
                      max={0.99}
                      defaultValue={c.toleranciaInicial}
                    />
                  )}
                </Campo>
                <Campo
                  label="Semáforo: verde a partir de"
                  erro={erros.semaforoVerdeMin}
                  dica="Razão previsto ÷ real (0,95 = 95%)."
                >
                  {(p) => (
                    <Input
                      {...p}
                      name="semaforoVerdeMin"
                      type="number"
                      step="0.01"
                      min={0}
                      max={1}
                      defaultValue={c.semaforoVerdeMin}
                    />
                  )}
                </Campo>
                <Campo
                  label="Semáforo: âmbar a partir de"
                  erro={erros.semaforoAmareloMin}
                  dica="Abaixo disso é vermelho."
                >
                  {(p) => (
                    <Input
                      {...p}
                      name="semaforoAmareloMin"
                      type="number"
                      step="0.01"
                      min={0}
                      max={1}
                      defaultValue={c.semaforoAmareloMin}
                    />
                  )}
                </Campo>
                <Campo
                  label="Pontualidade: tolerância (min)"
                  erro={erros.pontualidadeToleranciaMin}
                  dica="Atraso até aqui não desconta."
                >
                  {(p) => (
                    <Input
                      {...p}
                      name="pontualidadeToleranciaMin"
                      type="number"
                      min={0}
                      defaultValue={c.pontualidadeToleranciaMin}
                    />
                  )}
                </Campo>
                <Campo
                  label="Pontualidade: atraso máximo (min)"
                  erro={erros.pontualidadeAtrasoMaximoMin}
                  dica="A partir daqui a pontualidade do dia vale zero."
                >
                  {(p) => (
                    <Input
                      {...p}
                      name="pontualidadeAtrasoMaximoMin"
                      type="number"
                      min={1}
                      defaultValue={c.pontualidadeAtrasoMaximoMin}
                    />
                  )}
                </Campo>
              </div>
              <fieldset className="flex flex-col gap-3 rounded-xl border border-border p-4">
                <legend className="px-1 text-sm font-semibold">Faltas e atestado</legend>
                <Checkbox
                  name="atestadoContaComoFalta"
                  label="Atestado conta como falta (desmarcado: o atestado abona o dia)"
                  defaultChecked={c.atestadoContaComoFalta}
                />
                <Checkbox
                  name="faltaEntraNaNota"
                  label="Falta entra na média com a nota mínima (desmarcado: quem bate a meta nos outros dias recupera)"
                  defaultChecked={c.faltaEntraNaNota}
                />
                <Campo
                  label="Faltas no período que tiram a faixa"
                  erro={erros.faltasParaPerderFaixa}
                  dica="Ex.: 1 = faltou na semana, fica sem bônus da semana. Vazio = a falta não tira a faixa."
                >
                  {(p) => (
                    <Input
                      {...p}
                      name="faltasParaPerderFaixa"
                      type="number"
                      min={1}
                      className="max-w-32"
                      defaultValue={c.faltasParaPerderFaixa ?? ''}
                    />
                  )}
                </Campo>
              </fieldset>
              <Checkbox
                name="exibirIndicadorAoOperador"
                label="Mostrar ao operador o próprio indicador no resumo do dia (nunca o bônus)"
                defaultChecked={c.exibirIndicadorAoOperador}
              />
              <Checkbox
                name="exibirIndividualNoPainel"
                label="Mostrar na TV o nome de quem está em cada posto (cláusula 8.4: só com opt-in)"
                defaultChecked={c.exibirIndividualNoPainel}
              />
              {podeEditar && (
                <Button type="submit" className="self-start" carregando={salvar.isPending}>
                  Salvar parâmetros
                </Button>
              )}
            </fieldset>
          </Formulario>
        )}
      </div>
    </section>
  )
}
